<template>
  <shortcut-overlay-shell v-if="isOpen" @close="commandPalette.close()">
    <div class="command-palette">
      <fs-input
        ref="inputEl"
        v-model="query"
        class="command-palette__search"
        :placeholder="placeholder"
        is-search
        auto-focus
      >
        <template v-if="breadcrumb" #prefix>
          <button type="button" class="command-palette__breadcrumb" title="Back to all commands" @click="goBack()">
            <span class="command-palette__breadcrumb-icon" v-html="caretLeftIcon" />
            {{ breadcrumb }}
          </button>
        </template>
      </fs-input>

      <div
        ref="resultsEl"
        :class="{ 'command-palette__results': true, 'command-palette__results--thumbnails': showsThumbnails }"
      >
        <template v-for="group in groupedResults" :key="group.name">
          <div v-if="group.showTitle" class="command-palette__group-title">{{ group.name }}</div>
          <component
            :is="entry.to ? 'a' : 'button'"
            v-for="{ entry, index } in group.entries"
            :key="entry.id"
            v-bind="entry.to ? { href: href(entry) } : { type: 'button' }"
            :data-index="index"
            :class="{
              'command-palette__row': true,
              'command-palette__row--highlighted': index === highlightIndex,
            }"
            @mouseenter="highlightIndex = index"
            @click="onRowClick(entry, $event)"
          >
            <img
              v-if="entry.image && !brokenImages.has(entry.id)"
              class="command-palette__row-image"
              :src="entry.image"
              alt=""
              @error="brokenImages.add(entry.id)"
            />
            <span
              v-else-if="entry.icon"
              class="command-palette__row-icon"
              :style="entry.iconScale ? { '--palette-icon-scale': entry.iconScale } : undefined"
              v-html="entry.icon"
            />
            <span class="command-palette__row-text">
              <span class="command-palette__row-label">{{ entry.label }}</span>
              <span v-if="entry.detail" class="command-palette__row-detail">{{ entry.detail }}</span>
            </span>
            <kbd-combo v-if="entry.shortcut" class="command-palette__row-shortcut" :combo="entry.shortcut" />
            <span v-if="entry.enter" class="command-palette__row-chevron" v-html="caretRightIcon" />
          </component>
        </template>

        <p v-if="!results.length" class="command-palette__empty">{{ emptyMessage }}</p>
      </div>

      <footer class="command-palette__footer">
        <span class="command-palette__legend"><kbd-combo combo="enter" /> {{ openLegend }}</span>
        <span v-if="canOpenInNewTab" class="command-palette__legend">
          <kbd-combo combo="mod+enter" /> open in new tab
        </span>
        <span v-if="canGoBack" class="command-palette__legend"><kbd-combo combo="Escape" /> back</span>
      </footer>
    </div>
  </shortcut-overlay-shell>
</template>

<script setup lang="ts">
import FsInput from '@components/form-elements/fs-input.vue';
import KbdCombo from '@components/shortcuts/kbd-combo.vue';
import ShortcutOverlayShell from '@components/shortcuts/shortcut-overlay-shell.vue';
import { commandPalette, useBrandEntries, usePaletteEntries, type PaletteEntry } from '@lib/command-palette';
import { entitySearchHitIcon, entitySearchKind } from '@lib/palette-entity-search';
import { filterByTerms } from '@lib/list-filter';
import { openRouteInNewTab } from '@lib/router-new-tab';
import { useShortcutLayer } from '@lib/shortcuts';
import { useApi } from '@lib/api';
import { useUser } from '@/store/user';
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';
import debounce from 'lodash/debounce';

import caretLeftIcon from '@icons/other2/caret-left.svg';
import caretRightIcon from '@icons/other2/caret-right.svg';

const isOpen = commandPalette.isOpen;
const view = commandPalette.view;
const router = useRouter();
const api = useApi();

// fs-input writes null when its clear button is pressed, hence the nullable model.
const query = ref<string | null>('');
const term = computed(() => (query.value ?? '').trim());
const highlightIndex = ref(0);
const resultsEl = useTemplateRef('resultsEl');
const inputEl = useTemplateRef<{ focus: () => void }>('inputEl');

const entries = usePaletteEntries();
const brandEntries = useBrandEntries();

// Entity search — the sub-menus that ask the backend instead of filtering a local list.
const searchKind = computed(() => (view.value.id === 'search' ? entitySearchKind(view.value.entity) : null));
const searchHits = ref<PaletteEntry[]>([]);
const searchPending = ref(false);
// A logo or product photo that 404s would render as a broken image; fall back to the row's icon.
const brokenImages = ref(new Set<string>());
let searchToken: ReturnType<typeof axios.CancelToken.source> | null = null;

async function runEntitySearch() {
  const kind = searchKind.value;
  const searchTerm = term.value;
  searchToken?.cancel();
  if (!kind || !searchTerm) {
    searchHits.value = [];
    searchPending.value = false;
    return;
  }

  searchToken = axios.CancelToken.source();
  try {
    const hits = await api.search_SearchEntities(useUser().brandId, kind.type, searchTerm, undefined, {
      cancelToken: searchToken.token,
    });
    searchHits.value = hits.map((hit) => ({
      id: `${kind.label}-${hit.id}`,
      label: hit.title,
      detail: hit.subtitle ?? undefined,
      group: 'Results' as const,
      ...entitySearchHitIcon(kind, hit),
      to: () => kind.route(hit),
    }));
    searchPending.value = false;
  } catch (error) {
    if (axios.isCancel(error)) return;
    searchHits.value = [];
    searchPending.value = false;
    throw error;
  }
}

const debouncedEntitySearch = debounce(runEntitySearch, 200, { leading: true, trailing: true });

const results = computed<PaletteEntry[]>(() => {
  if (view.value.id === 'search') return searchHits.value;

  const source = view.value.id === 'brands' ? brandEntries.value : entries.value;
  // Without a query, keep the root list scannable: navigation, search and actions only.
  // The (many) admin subpages appear as soon as the user types.
  if (!query.value) return source.filter((entry) => entry.group !== 'Admin');
  const ranked = filterByTerms(
    source,
    query.value,
    (entry) => `${entry.label} ${entry.keywords ?? ''}`,
    (entry) => entry.label
  );
  // Ranking can interleave groups, but rows render grouped and the highlight walks this list
  // in order — keep each group contiguous, groups ordered by their best-ranked entry.
  return [...new Set(ranked.map((entry) => entry.group))].flatMap((group) =>
    ranked.filter((entry) => entry.group === group)
  );
});

const groupedResults = computed(() => {
  const groups: {
    name: PaletteEntry['group'];
    showTitle: boolean;
    entries: { entry: PaletteEntry; index: number }[];
  }[] = [];
  results.value.forEach((entry, index) => {
    const group = groups.find((g) => g.name === entry.group);
    if (group) group.entries.push({ entry, index });
    // Inside a sub-menu the list is the group — a single heading over everything says nothing.
    else groups.push({ name: entry.group, showTitle: view.value.id === 'root', entries: [{ entry, index }] });
  });
  return groups;
});

const breadcrumb = computed(() => {
  if (view.value.id === 'brands') return 'Switch brand';
  return searchKind.value?.breadcrumb ?? '';
});

const placeholder = computed(() => {
  if (view.value.id === 'brands') return 'Search brands…';
  return searchKind.value?.placeholder ?? 'Where to?';
});

const emptyMessage = computed(() => {
  const kind = searchKind.value;
  if (!kind) return 'No matches';
  if (!term.value) return `Type to search ${kind.noun}`;
  return searchPending.value ? 'Searching…' : 'No matches';
});

// Sub-menu rows are entities with pictures — a product photo or a brand logo — so they get
// a wider icon column than the root menu's glyphs.
const showsThumbnails = computed(() => view.value.id !== 'root');
// Escape only widens to the root menu when that is where the sub-menu was opened from.
const canGoBack = computed(() => view.value.id !== 'root' && commandPalette.cameFromRoot.value);
const canOpenInNewTab = computed(() => results.value.some((entry) => entry.to || entry.runInNewTab));
const openLegend = computed(() => (results.value[highlightIndex.value]?.enter ? 'choose' : 'open'));

watch(isOpen, (open) => {
  if (open) query.value = '';
});

watch(view, async () => {
  query.value = '';
  searchHits.value = [];
  await nextTick();
  inputEl.value?.focus();
});

watch(query, () => {
  if (view.value.id !== 'search') return;
  searchPending.value = Boolean(term.value);
  debouncedEntitySearch();
});

watch(results, () => (highlightIndex.value = 0));

watch(highlightIndex, async (index) => {
  await nextTick();
  resultsEl.value?.querySelector(`[data-index="${index}"]`)?.scrollIntoView({ block: 'nearest' });
});

function href(entry: PaletteEntry) {
  return entry.to ? router.resolve(entry.to()).href : undefined;
}

function goBack() {
  commandPalette.back();
}

function run(entry: PaletteEntry) {
  if (entry.enter) {
    commandPalette.enter(entry.enter);
    return;
  }
  // Close first so the palette's layer is gone before the entry opens
  // anything with a layer of its own (e.g. the shortcut help overlay).
  commandPalette.close();
  if (entry.to) router.push(entry.to());
  else entry.run();
}

function openInNewTab(entry: PaletteEntry) {
  if (!entry.to && !entry.runInNewTab) return;
  commandPalette.close();
  if (entry.to) openRouteInNewTab(router, entry.to());
  else entry.runInNewTab!();
}

function onRowClick(entry: PaletteEntry, event: MouseEvent) {
  const modified = event.ctrlKey || event.metaKey || event.shiftKey || event.altKey;
  // A modified click on a link is the browser's to handle (new tab/window);
  // leave the palette open so the user stays where they were.
  if (entry.to && modified) return;
  event.preventDefault();
  // An acting entry is a button, so the browser does nothing with the modifier — the
  // entry says what its new-tab means and we do it ourselves.
  if (modified && entry.runInNewTab) openInNewTab(entry);
  else run(entry);
}

function highlightedEntry(): PaletteEntry | undefined {
  return results.value[highlightIndex.value] ?? results.value[0];
}

function moveHighlight(delta: number) {
  const count = results.value.length;
  if (!count) return;
  highlightIndex.value = (highlightIndex.value + delta + count) % count;
}

function closeOrBack() {
  if (canGoBack.value) goBack();
  else commandPalette.close();
}

const { register } = useShortcutLayer(isOpen, 'command-palette', { blockFallthrough: true });
register('Escape', () => closeOrBack(), { allowInInput: true });
// Backspace on an empty query leaves the sub-menu, the way a path segment is deleted.
// Declining (false) hands the key back to the input for every other case.
register(
  'Backspace',
  () => {
    if (view.value.id === 'root' || query.value) return false;
    goBack();
  },
  { allowInInput: true }
);
register('mod+k', () => commandPalette.close(), { allowInInput: true });
register('ArrowDown', () => moveHighlight(1), { allowInInput: true });
register('ArrowUp', () => moveHighlight(-1), { allowInInput: true });
register(
  'Enter',
  () => {
    const entry = highlightedEntry();
    if (entry) run(entry);
  },
  { allowInInput: true }
);
register(
  'mod+Enter',
  () => {
    const entry = highlightedEntry();
    if (entry) openInNewTab(entry);
  },
  { allowInInput: true }
);
</script>

<style scoped lang="scss">
@import '@styles/variables';

.command-palette {
  display: flex;
  flex-direction: column;
  min-height: 0;

  // The pill-shaped field is flattened into a header row: the panel already frames
  // the input, so a second frame inside it just adds weight.
  &__search {
    height: 5.6rem;
    flex-shrink: 0;
    border-radius: 0;
    border-bottom: 1px solid $c-hairline;

    &,
    &:hover,
    &.fs-input--focus,
    &.fs-input--focus:hover {
      background: none;
    }

    :deep(.fs-input__text-input-label) {
      gap: 0.8rem;
      padding: 0 1.8rem;
    }

    :deep(.fs-input__search-icon) {
      width: 1.6rem;
      height: 1.6rem;
      margin-right: 0;

      svg {
        fill: $c-ink-soft;
      }
    }

    :deep(.fs-input__prefix) {
      margin: 0;
    }

    :deep(.input-itself) {
      flex: 1;
      width: auto;
      font-size: 1.6rem;
      line-height: 2.2rem;

      &::placeholder {
        color: rgba($c-ink, 0.38);
      }
    }
  }

  &__breadcrumb {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    max-width: 22rem;
    padding: 0.4rem 0.8rem 0.4rem 0.5rem;
    border: none;
    border-radius: 0.6rem;
    background: $c-surface-muted;
    color: $c-ink;
    font-family: inherit;
    font-size: 1.3rem;
    line-height: 1.6rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    cursor: pointer;

    &:hover {
      background: $c-canvas-deep;
    }
  }

  &__breadcrumb-icon {
    display: inline-flex;
    width: 1.2rem;
    height: 1.2rem;
    flex-shrink: 0;

    :deep(svg) {
      width: 100%;
      height: 100%;
      fill: $c-ink-soft;
    }
  }

  &__results {
    overflow-y: auto;
    padding: 0.6rem;
    scrollbar-width: thin;
  }

  &__group-title {
    padding: 1rem 1rem 0.4rem;
    color: $c-ink-soft;
    font-size: 1.1rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }

  &__row {
    display: flex;
    align-items: center;
    gap: 1.2rem;
    width: 100%;
    padding: 0.8rem 1rem;
    border: none;
    border-radius: 0.8rem;
    background: none;
    color: $c-ink;
    font-family: inherit;
    font-size: 1.4rem;
    text-align: left;
    text-decoration: none;
    cursor: pointer;

    &--highlighted {
      background: $c-surface-muted;
    }
  }

  &__row-icon,
  &__row-image {
    width: 1.7rem;
    height: 1.7rem;
    flex-shrink: 0;
  }

  &__row-icon {
    display: inline-flex;
    color: $c-ink-soft;

    :deep(svg) {
      width: 100%;
      height: 100%;
      // The icons are shared with the navbar and the admin nav, which recolour them the same way.
      fill: currentcolor;
      // 1 unless the entry asks for more — see `iconScale`, for artwork drawn inside its own padding.
      transform: scale(var(--palette-icon-scale, 1));
    }
  }

  &__row-image {
    border-radius: 0.3rem;
    object-fit: contain;
  }

  &__results--thumbnails &__row-icon,
  &__results--thumbnails &__row-image {
    width: 2.8rem;
    height: 2.8rem;
  }

  &__row-text {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
  }

  &__row-label,
  &__row-detail {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__row-detail {
    color: $c-ink-soft;
    font-size: 1.2rem;
  }

  &__row-shortcut {
    flex-shrink: 0;
  }

  &__row-chevron {
    display: inline-flex;
    width: 1.2rem;
    height: 1.2rem;
    flex-shrink: 0;

    :deep(svg) {
      width: 100%;
      height: 100%;
      fill: rgba($c-ink, 0.3);
    }
  }

  &__empty {
    margin: 0;
    padding: 3rem 1.2rem;
    color: $c-ink-soft;
    font-size: 1.4rem;
    text-align: center;
  }

  &__footer {
    display: flex;
    gap: 1.6rem;
    flex-shrink: 0;
    padding: 0.8rem 1.8rem;
    border-top: 1px solid $c-hairline;
    background: $c-canvas;
  }

  &__legend {
    display: inline-flex;
    align-items: center;
    gap: 0.6rem;
    color: $c-ink-soft;
    font-size: 1.2rem;
  }
}
</style>
